import { useQuery } from '@apollo/client';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import React, { useContext, useEffect, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import SubheadingText from '@ui/atoms/SubheadingText';

import DashboardTask from '@ui/molecules/DashboardTask';

import { IAuthenticatedMember, SubscriptionInfo } from '@hooks/useMemberInfo';
import useWindowSize from '@hooks/useWindowSize';

import { keysToCamel } from '@utils/butter';
import { doesNotExist } from '@utils/general';
import { combineMemberFragments } from '@utils/graphql';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { FolxMembershipStatus } from '@data/membership';

import { IPastAppointments, IUpcomingAppointments } from '@folx-types/scheduledAppointments';

import { membership, membershipName, queryName } from '@graphql/fragments/membership';
import PAST_APPOINTMENTS_QUERY from '@graphql/queries/PastAppointments.query.gql';
import UPCOMING_APPOINTMENTS_QUERY from '@graphql/queries/UpcomingAppointments.query.gql';

import theme from '@theme/FolxTheme';

import EmergencyContactTask from '../EmergencyContactTask';
import HealthHistoryTask from '../HealthHistoryTask';
import JoinCommunityTask from '../JoinCommunityTask';
import ScheduleAppointmentTask from '../ScheduleAppointmentTask';
import tasks, {
	BOOK_VISIT,
	CANCELLATION_COMPLETE,
	CANCELLED,
	EMERGENCY_CONTACT,
	HEALTH_HISTORY,
	IDashboardTask,
	JOIN_COMMUNITY,
	PAST_DUE,
} from '../Tasks.data';

const SectionHeader = styled(SubheadingText)`
	grid-column-start: span 2;
	grid-column-end: auto;
	margin-top: ${theme.spacing.lg};
`;

const HeadingRow = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;

	${BasicText} {
		align-self: flex-end;
	}
`;

interface ITaskContent {
	heading: string;
	description: string;
	image: string;
	alt: string;
	contentKey: string;
}

interface ITaskSectionContent {
	tasks: ITaskContent[];
}

const TasksSection = () => {
	const [taskInfo, setTaskInfo] = useState<Record<string, IDashboardTask>>({ ...tasks });
	const [combinedTasks, setCombinedTasks] = useState<ITaskContent[]>([]);
	const [completed, setCompleted] = useState<ITaskContent[]>([]);
	const [membershipStatus, setMembershipStatus] = useState<FolxMembershipStatus>();
	const [hasAppointment, setHasAppointment] = useState(false);

	const { isMobile } = useWindowSize();

	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "dashboard-task-section-page" }) {
				tasks {
					heading
					description
					image
					alt
					content_key
				}
			}
		}
	`);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const { data: membershipData, loading: membershipLoading } = useQuery<
		IAuthenticatedMember<SubscriptionInfo>
	>(combineMemberFragments([membershipName], [membership], queryName), {
		client: graphQLv2Client,
	});

	const { loading: loadingUpcomingAppointments, data: upcomingAppointmentsData } =
		useQuery<IUpcomingAppointments>(UPCOMING_APPOINTMENTS_QUERY, {
			fetchPolicy: 'no-cache',
		});

	const { loading: loadingPastAppointments, data: pastAppointmentsData } =
		useQuery<IPastAppointments>(PAST_APPOINTMENTS_QUERY, {
			fetchPolicy: 'no-cache',
			variables: {
				first: 1,
			},
		});

	const { tasks: taskContent } = keysToCamel(pageContent) as ITaskSectionContent;

	const updateTaskStatus = (item: string, loading: boolean, done: boolean) => {
		const contentKey = Object.keys(taskInfo).find((key) => taskInfo[key].id === item) || '';
		setTaskInfo({ ...taskInfo, [contentKey]: { ...(taskInfo[contentKey] ?? {}), loading, done } });
	};

	const computeCombinedTasks = () => {
		let combinedTasksUpdate: ITaskContent[] = [];
		let completedUpdate: ITaskContent[] = [];

		if (membershipStatus === FolxMembershipStatus.ACTIVE) {
			const activeTaskContent = taskContent.filter(
				(task) => !['cancellationComplete', 'cancelled', 'pastDue'].includes(task.contentKey)
			);
			completedUpdate = activeTaskContent
				.filter((task) => taskInfo[task.contentKey]?.done)
				.sort((a, b) => taskInfo[a.contentKey]?.priority - taskInfo[b.contentKey]?.priority);

			const incomplete = activeTaskContent
				.filter((task) => !taskInfo[task.contentKey]?.done)
				.sort((a, b) => taskInfo[a.contentKey]?.priority - taskInfo[b.contentKey]?.priority);

			combinedTasksUpdate = [...incomplete, ...completedUpdate];

			if (!hasAppointment) {
				combinedTasksUpdate = combinedTasksUpdate.filter(
					(task) => task.contentKey !== 'healthHistory'
				);
			}
		} else if (membershipStatus == FolxMembershipStatus.CANCELING) {
			combinedTasksUpdate = taskContent.filter((task) => task.contentKey === 'cancelled');
		} else if (membershipStatus == FolxMembershipStatus.CANCELED) {
			combinedTasksUpdate = taskContent.filter(
				(task) => task.contentKey === 'cancellationComplete'
			);
		} else if (membershipStatus == FolxMembershipStatus.PAST_DUE) {
			combinedTasksUpdate = taskContent.filter((task) => task.contentKey === 'pastDue');
		}

		setCombinedTasks(combinedTasksUpdate);
		setCompleted(completedUpdate);
	};

	useEffect(() => {
		if (doesNotExist(upcomingAppointmentsData) || doesNotExist(pastAppointmentsData)) {
			return;
		}
		const upcomingAppointments =
			upcomingAppointmentsData?.upcomingAppointmentsForCurrentMember?.nodes || [];
		const pastAppointments = pastAppointmentsData?.pastAppointmentsForCurrentMember?.edges || [];

		const hasAppointmentUpdate = !(
			doesNotExist(upcomingAppointments) && doesNotExist(pastAppointments)
		);
		setHasAppointment(hasAppointmentUpdate);
		updateTaskStatus(BOOK_VISIT, false, hasAppointmentUpdate);
	}, [loadingUpcomingAppointments, loadingPastAppointments]);

	const computeMembershipStatus = () => {
		if (membershipLoading || doesNotExist(membershipData)) return;

		const membershipSubscription = membershipData?.authenticatedMember.membershipSubscription;
		let membershipStatusUpdate =
			FolxMembershipStatus[
				(membershipSubscription?.status as FolxMembershipStatus) ?? FolxMembershipStatus.UNKNOWN
			] ?? FolxMembershipStatus.UNKNOWN;

		if (membershipSubscription?.cancelAt) {
			membershipStatusUpdate = FolxMembershipStatus.CANCELING;
		} else if (membershipSubscription?.canceledAt) {
			membershipStatusUpdate = FolxMembershipStatus.CANCELED;
		}

		setMembershipStatus(membershipStatusUpdate);
	};

	useDeepCompareEffect(computeMembershipStatus, [membershipLoading]);

	useEffect(computeCombinedTasks, [membershipStatus]);

	useDeepCompareEffect(computeCombinedTasks, [taskInfo]);

	const tasksLoading = combinedTasks.some((task) => taskInfo[task.contentKey]?.loading);

	const taskRenderer = ({ contentKey, heading, description, image, alt }: ITaskContent) => {
		switch (tasks[contentKey]?.id) {
			case BOOK_VISIT:
				return (
					<ScheduleAppointmentTask
						key={taskInfo[contentKey]?.id ?? ''}
						heading={heading}
						description={description}
						image={image}
						alt={alt}
						updateTaskStatus={updateTaskStatus}
						done={hasAppointment}
						loading={tasksLoading}
					/>
				);
			case HEALTH_HISTORY:
				return (
					<HealthHistoryTask
						key={taskInfo[contentKey]?.id ?? ''}
						heading={heading}
						description={description}
						image={image}
						alt={alt}
						updateTaskStatus={updateTaskStatus}
						loading={tasksLoading}
					/>
				);
			case EMERGENCY_CONTACT:
				return (
					<EmergencyContactTask
						key={taskInfo[contentKey]?.id ?? ''}
						heading={heading}
						description={description}
						image={image}
						alt={alt}
						updateTaskStatus={updateTaskStatus}
						loading={tasksLoading}
					/>
				);
			case JOIN_COMMUNITY:
				return (
					<JoinCommunityTask
						key={taskInfo[contentKey]?.id ?? ''}
						heading={heading}
						description={description}
						image={image}
						alt={alt}
						updateTaskStatus={updateTaskStatus}
						loading={tasksLoading}
					/>
				);
			case CANCELLED:
				return (
					<DashboardTask
						key={taskInfo[contentKey]?.id ?? ''}
						heading={heading}
						description={description}
						image={image}
						alt={alt}
						onClick={() => navigate(`/app/member/reactivate_membership/summary/?backTo=billing`)}
					/>
				);
			case CANCELLATION_COMPLETE:
				return (
					<DashboardTask
						key={taskInfo[contentKey]?.id ?? ''}
						heading={heading}
						description={description}
						image={image}
						alt={alt}
						onClick={() => navigate(`/app/member/reactivate_membership/summary`)}
					/>
				);
			case PAST_DUE:
				return (
					<DashboardTask
						key={taskInfo[contentKey]?.id ?? ''}
						heading={heading}
						description={description}
						image={image}
						alt={alt}
						onClick={() => navigate(`/app/member/billing/bills`)}
					/>
				);
		}
	};

	const allDone = combinedTasks.every((task) => taskInfo[task.contentKey]?.done);

	if (allDone) return;

	return (
		<>
			<HeadingRow>
				<SectionHeader>Tasks</SectionHeader>
				{!tasksLoading && (
					<BasicText variant="fineprint">
						{completed.length} of {combinedTasks.length} completed
					</BasicText>
				)}
			</HeadingRow>
			{isMobile ? (
				<Swiper
					slidesPerView={'auto'}
					spaceBetween={18}
					slidesOffsetAfter={32}
					slidesOffsetBefore={32}
					autoHeight
					style={{
						marginLeft: `-${theme.spacing.lg}`,
						marginRight: `-${theme.spacing.smmd}`,
						overflow: 'visible',
					}}>
					{combinedTasks.map((task) => (
						<SwiperSlide
							style={{ width: 'auto', alignSelf: 'stretch', height: 'auto' }}
							key={task.contentKey}>
							{taskRenderer(task)}
						</SwiperSlide>
					))}
				</Swiper>
			) : (
				<FlexColumn gap="sm">{combinedTasks.map(taskRenderer)}</FlexColumn>
			)}
		</>
	);
};

export default TasksSection;
